import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Video, Plus, Heart, Clock, Share2, RefreshCw,
  Play, Pause, ChevronRight, User, BookOpen
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const LegacyVideoMessages = () => {
  const [videos, setVideos] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    recipient: '',
    prompt: ''
  });
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase46/legacy-videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPrompts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase46/legacy-videos/prompts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrompts(data.prompts || []);
      }
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchVideos();
    fetchPrompts();
  }, [fetchVideos, fetchPrompts]);

  const createVideo = async () => {
    try {
      const res = await fetch(`${API}/api/phase46/legacy-videos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newVideo)
      });
      
      if (res.ok) {
        toast({ 
          title: "Video Created", 
          description: "Your legacy video recording has been saved" 
        });
        setShowCreateForm(false);
        setNewVideo({ title: '', description: '', recipient: '', prompt: '' });
        fetchVideos();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to create video", variant: "destructive" });
    }
  };

  const selectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setNewVideo({
      ...newVideo,
      prompt: prompt,
      title: prompt.substring(0, 50) + (prompt.length > 50 ? '...' : '')
    });
    setShowCreateForm(true);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'childhood': return '👶';
      case 'family': return '👨‍👩‍👧‍👦';
      case 'life_lessons': return '💡';
      case 'career': return '💼';
      case 'love': return '❤️';
      case 'gratitude': return '🙏';
      default: return '📝';
    }
  };

  const getCategoryTitle = (category) => {
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="legacy-video-messages">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Video className="h-7 w-7 text-teal-600" />
            Legacy Video Messages
          </h1>
          <p className="text-gray-600">Record and share meaningful messages with loved ones</p>
        </div>
        <Button 
          onClick={() => setShowCreateForm(true)}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="new-video-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Recording
        </Button>
      </div>

      {/* Inspirational Banner */}
      <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Heart className="h-6 w-6 text-rose-500 mt-1" />
            <div>
              <h3 className="font-semibold text-rose-900">Create Lasting Memories</h3>
              <p className="text-sm text-rose-700">
                Your words and stories are precious gifts. Record messages that your family will treasure forever.
                Use the prompts below for inspiration, or speak from your heart.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'videos' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('videos')}
          className={activeTab === 'videos' ? 'bg-teal-600' : ''}
        >
          <Video className="h-4 w-4 mr-2" />
          My Videos ({videos.length})
        </Button>
        <Button
          variant={activeTab === 'prompts' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('prompts')}
          className={activeTab === 'prompts' ? 'bg-teal-600' : ''}
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Recording Prompts
        </Button>
      </div>

      {/* Videos List */}
      {activeTab === 'videos' && (
        <div className="grid gap-4">
          {videos.length > 0 ? (
            videos.map((video) => (
              <Card 
                key={video.id}
                className="hover:border-teal-300 transition-colors"
                data-testid={`video-${video.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Thumbnail Placeholder */}
                    <div className="w-32 h-24 bg-gradient-to-br from-teal-100 to-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {video.status === 'completed' ? (
                        <Play className="h-8 w-8 text-teal-600" />
                      ) : (
                        <Video className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{video.title}</h3>
                        <Badge 
                          className={
                            video.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }
                        >
                          {video.status === 'completed' ? 'Completed' : 'Draft'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{video.description}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          For: {video.recipient}
                        </span>
                        {video.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {video.duration}
                          </span>
                        )}
                        <span>
                          {new Date(video.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {video.status === 'completed' ? (
                        <>
                          <Button variant="outline" size="sm">
                            <Play className="h-4 w-4 mr-1" />
                            Play
                          </Button>
                          <Button variant="outline" size="sm">
                            <Share2 className="h-4 w-4 mr-1" />
                            Share
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                          <Video className="h-4 w-4 mr-1" />
                          Record
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Video className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">No Videos Yet</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Start recording your first legacy message
                </p>
                <Button 
                  onClick={() => setShowCreateForm(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  Create Your First Video
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Prompts */}
      {activeTab === 'prompts' && (
        <div className="space-y-6">
          {prompts.map((category) => (
            <Card key={category.category}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="text-2xl">{getCategoryIcon(category.category)}</span>
                  {getCategoryTitle(category.category)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {category.prompts.map((prompt, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-teal-50 cursor-pointer transition-colors"
                      onClick={() => selectPrompt(prompt)}
                    >
                      <p className="text-gray-700">{prompt}</p>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5 text-teal-600" />
                New Legacy Video
              </CardTitle>
              <CardDescription>
                Record a message for your loved ones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPrompt && (
                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <p className="text-sm font-medium text-teal-900">Prompt:</p>
                  <p className="text-teal-700">{selectedPrompt}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  placeholder="Give your message a title"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">For Whom?</label>
                <Input
                  value={newVideo.recipient}
                  onChange={(e) => setNewVideo({ ...newVideo, recipient: e.target.value })}
                  placeholder="e.g., My grandchildren, Maria, Everyone"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Description (Optional)</label>
                <Textarea
                  value={newVideo.description}
                  onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                  placeholder="What is this message about?"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={createVideo} 
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  disabled={!newVideo.title || !newVideo.recipient}
                >
                  <Video className="h-4 w-4 mr-2" />
                  Create & Start Recording
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setSelectedPrompt(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LegacyVideoMessages;
